import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { AreaChart } from "../../../components/ui/AreaChart";
import { Card } from "../../../components/ui/Card";
import { Select } from "../../../components/ui/Select";
import { EmptyState, ErrorState, LoadingState } from "../../../components/ui/State";
import { ApiError } from "../../../lib/api";
import { networkViolationsApi } from "../../../lib/api/network/violations";
import { t } from "../../../lib/i18n";
import { presetRange, useInsightsScope } from "../../../lib/useInsightsScope";
import { useSearchParams } from "react-router-dom";

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

function formatLabel(ts: string, bucket: "hour" | "day"): string {
  const d = new Date(ts);
  if (bucket === "hour") {
    return `${d.getUTCMonth() + 1}/${d.getUTCDate()} ${String(d.getUTCHours()).padStart(2, "0")}:00`;
  }
  return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
}

export function ViolationsTimelineTab() {
  const [params, setParams] = useSearchParams();
  const scope = useInsightsScope();
  const bucket = (params.get("bucket") as "hour" | "day") || "day";

  // If the workspace-level scope hasn't picked a date range yet, default to
  // the last 7 days so the chart is never blank on first land.
  const effectiveRange = scope.from && scope.to ? { from: scope.from, to: scope.to } : presetRange(7);

  const query = useQuery({
    queryKey: [
      "violations",
      "timeline",
      effectiveRange.from,
      effectiveRange.to,
      bucket,
      scope.device_group,
      scope.platform,
      scope.device,
    ],
    queryFn: () =>
      networkViolationsApi.timeline(
        {
          ...scope,
          from: effectiveRange.from,
          to: effectiveRange.to,
        },
        bucket,
      ),
  });

  const series = query.data?.series ?? [];
  const values = useMemo(() => series.map((p) => p.count), [series]);
  const labels = useMemo(() => series.map((p) => formatLabel(p.ts, bucket)), [series, bucket]);
  const total = useMemo(() => values.reduce((acc, v) => acc + v, 0), [values]);

  return (
    <div className="stack">
      <div className="filter-bar">
        <Select
          value={bucket}
          onChange={(e) => {
            const next = new URLSearchParams(params);
            next.set("bucket", e.target.value);
            setParams(next);
          }}
          aria-label={t("insights.violations.timeline.bucket")}
        >
          <option value="day">{t("insights.violations.timeline.bucket.day")}</option>
          <option value="hour">{t("insights.violations.timeline.bucket.hour")}</option>
        </Select>
        <span className="muted" style={{ marginLeft: "auto", fontSize: 13 }}>
          {t("insights.violations.timeline.totalLabel")}: <strong>{total.toLocaleString()}</strong>
        </span>
      </div>

      <Card title={t("insights.violations.timeline.cardTitle")}>
        {query.isLoading && <LoadingState />}
        {query.error && (
          <ErrorState titleKey="states.errorTitle" message={translateError(query.error)} />
        )}
        {query.data && series.length === 0 && (
          <EmptyState
            titleKey="insights.violations.timeline.empty.title"
            bodyKey="insights.violations.timeline.empty.body"
          />
        )}
        {query.data && series.length > 0 && (
          <AreaChart values={values} labels={labels} height={260} />
        )}
      </Card>
    </div>
  );
}
